import { useState, FormEvent, ChangeEvent, useMemo } from "react";
import { useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { TextareaFormGroup } from "../../common/textarea-form-group/textarea-form-group";
import { ServiceTemplateDetails } from "@idw-types/data/service-template-details/service-template-details";
import { createServiceTemplateReduxRequest } from "@redux-requests/service-templates/create-service-template";
import { getServiceTemplateValidationErrors } from "@utils/validation/service-template/service-template-validation-util";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import { RpServiceTemplate } from "@idw-types/data/reference-data/generic";

type AddServiceTemplateModalProps = {
    serviceTemplatesMap: { [serviceTemplateName: string]: RpServiceTemplate };
    onModalClose: () => void;
    axnCompanies: Array<AxnCompany>;
};

type AddServiceTemplateModalState = {
    requestInProgress: boolean;
    serviceTemplate: Partial<ServiceTemplateDetails>;
    selectedOrganizationOption: SelectOption;
};

const defaultServiceTemplate: Partial<ServiceTemplateDetails> = {
    name: "",
    description: "",
    ssoTemplate: ""
};


export const AddServiceTemplateModal = (props: AddServiceTemplateModalProps) => {
    const dispatch = useDispatch();
    const {
        serviceTemplatesMap,
        axnCompanies,
        onModalClose
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddServiceTemplateModalState>({
        requestInProgress: false,
        serviceTemplate: defaultServiceTemplate,
        selectedOrganizationOption: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });

        const newServiceTemplate = await createServiceTemplateReduxRequest(dispatch)(
            state.serviceTemplate, axnCompanies

        );
        if (newServiceTemplate) {
            onModalClose();
        } else {
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUpdateName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            serviceTemplate: {
                ...state.serviceTemplate,
                name: event.target.value
            }
        });
    };

    const handleUpdateDescription = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            serviceTemplate: {
                ...state.serviceTemplate,
                description: event.target.value
            }
        });
    };

    const handleUpdateTemplate = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            serviceTemplate: {
                ...state.serviceTemplate,
                ssoTemplate: event.target.value
            }
        });
    };

    const handleUpdateOrganizationOption = (newValue: SelectOption) => {
        setState({
            ...state,
            serviceTemplate: {
                ...state.serviceTemplate,
                organizationID: newValue?.value || null
            },
            selectedOrganizationOption: newValue
        });
    };

    const resource = resources.modals.addServiceTemplate;

    const validationErrors = useMemo(() => {
        const serviceTemplateDetails: ServiceTemplateDetails = {
            description: state.serviceTemplate.description,
            name: state.serviceTemplate.name,
            ssoTemplate: state.serviceTemplate.ssoTemplate,
            id: null,
            organizationID: null,
            status: EntityStatusTypes.ACTIVE,
            relyingPartyID: null
        };

        return getServiceTemplateValidationErrors(serviceTemplateDetails, serviceTemplatesMap, true);
    }, [state.serviceTemplate, serviceTemplatesMap]);

    return (
        <DefaultModal
            automationId="add-service-template-modal"
            ariaLabelledBy="add-service-template-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-service-template-title"} closeButton>
                    <Modal.Title>{resource.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputFormGroup
                        controlId="service-template-name-input"
                        label={resource.inputs.templateName.label}
                        ariaLabel={resource.inputs.templateName.label}
                        automationId="service-template-name-input"
                        placeholder={resource.inputs.templateName.placeholder}
                        value={state.serviceTemplate.name}
                        onChange={handleUpdateName}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    {validationErrors.metadata.nameError}

                    <OrganizationSelectFormGroup
                        idPrefix="service-template"
                        onChange={handleUpdateOrganizationOption}
                        axnCompanies={axnCompanies}
                        selectedOption={state.selectedOrganizationOption}
                        disabled={state.requestInProgress}
                        isRequired={false}
                        isSearchable={true}
                        isClearable={true}
                        labelWidth={labelWidth}
                    />

                    <InputFormGroup
                        controlId="service-template-description-input"
                        label={resource.inputs.description.label}
                        ariaLabel={resource.inputs.description.label}
                        automationId="service-template-description-input"
                        placeholder={resource.inputs.description.placeholder}
                        value={state.serviceTemplate.description}
                        onChange={handleUpdateDescription}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />

                    <TextareaFormGroup
                        controlId="service-template-template-input"
                        label={resource.inputs.template.label}
                        ariaLabel={resource.inputs.template.label}
                        automationId="service-template-template-input"
                        placeholder={resource.inputs.template.placeholder}
                        value={state.serviceTemplate.ssoTemplate}
                        onChange={handleUpdateTemplate}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />
                    {validationErrors.metadata.ssoTemplateError}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-service-template-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-service-template-save-button"
                        disabled={state.requestInProgress || validationErrors.count > 0}
                        hideSpinner={validationErrors.count > 0}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};